import React, { type FC, type MutableRefObject, useEffect, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/Shared/Inputs';
import CloseIcon from '@mui/icons-material/Close';
import { Spinner } from 'components/Shared';
import './CaseModelInfoDialog.scss';
import debounce from 'lodash/debounce';

interface CaseModelInfoDialogProps {
    open: boolean,
    caseModel: number,
    onClose: () => void,
    abridged?: boolean,
    averageHours?: number,
    ephemeral?: boolean,
}

export const CaseModelInfoDialog: FC<CaseModelInfoDialogProps> = (props) => {
    const { open, caseModel, onClose, abridged = false, averageHours, ephemeral } = props;
    const { t, ready, i18n } = useTranslation(['caseModelInfos', 'common'], { useSuspense: false });
    const modalRef = useRef<HTMLDivElement|null>(null) as MutableRefObject<HTMLDivElement|null>;
    let timeoutRef = useRef<number|null>(null) as MutableRefObject<number|null>;

    useEffect(() => {
        const closeOnMouseMove = debounce(() => onClose(), 500);
        if (open && ephemeral) {
            // we have to revert to using a timeout callback because the modal has a fade-in animation
            // that ensure the elements are not available in the DOM until after a few milliseconds
            timeoutRef.current = window.setTimeout(() => {
                // due to the animation delay, the ref object modalRef is always null, so we have to get it from here
                modalRef.current = document.querySelector('.MuiModal-root')!;
                if (open) {
                    // allow the modal to be closed by a mouse movement over the modal backdrop
                    modalRef?.current?.addEventListener('mousemove', closeOnMouseMove);
                    return () => {
                        clearTimeout(timeoutRef.current!);
                        timeoutRef.current = null
                        modalRef?.current?.removeEventListener('mousemove', closeOnMouseMove);
                    }
                }
            }, 150);
        }
    }, [open]);

    return (
        <Modal open={open} ref={modalRef} onClose={onClose} className='dialog_wrapper' role="dialog" aria-modal
            aria-labelledby="model_info_dialog_title" aria-describedby="model_info_dialog_content">
            <div className='dialog_surface'>
                <div className="dialog_title_wrapper">
                    <h2 id="model_info_dialog_title">
                        {ready && t('case_model_dialog_title', { replace: { modelNumber: caseModel.toString() }})}
                    </h2>
                    <IconButton aria-label={t('common:close')} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <div className="dialog_content_wrapper" id="model_info_dialog_content">
                {
                    ready
                    ? <div dangerouslySetInnerHTML={{__html: t(abridged ? "abridged" : caseModel.toString(), { averageHours })}}/>
                    : <Spinner/>
                }
                {
                    (caseModel <= 17 || caseModel === 21) &&
                    <div className="case_model_chart_wrapper">
                        <img src={`images/CaseModelCharts${i18n.language.slice(0, 2).toUpperCase()}/${caseModel}.png`} alt=""/>
                    </div>
                }
                </div>
                <div className="dialog_actions_wrapper">
                    <Button variant="opaque" onClick={onClose}>
                        {t('common:close')}
                    </Button>
                </div>
            </div>
        </Modal>
    )
}